import React, { useEffect, useRef, useState } from "react";
import { Alert, Input, Modal } from "antd";
import { useTranslation } from "react-i18next";
import { usePostExternalMutation } from "../../../redux/externals/externalsAPI";
import GETJwtToken from "../../../redux/authentication/authentication";
import type {
  ExternalEndpointOption,
  ExternalMutationResult,
} from "../../../interfaces/External.interface";
import ApiClientRoleSelect from "./ApiClientRoleSelect";
import ApiClientEndpointChecklist from "./ApiClientEndpointChecklist";
import type { ApiClientRoleOption } from "./apiClientRoles";
import { includesLiveRoute } from "./apiClientGrants";
import apiClientErrorMessage from "./apiClientErrors";

interface ApiClientCreateModalProps {
  open: boolean;
  orgId: string;
  roles: ApiClientRoleOption[];
  rolesLoading: boolean;
  /**
   * The endpoint catalog to grant from, or null when the caller may not read
   * it (no external-endpoints-view): the endpoint checklist is then left out.
   */
  endpoints: ExternalEndpointOption[] | null;
  /** No catalog has arrived yet: the list is replaced by a spinner. */
  endpointsLoading: boolean;
  /** The catalog is being read again: Create waits for it. */
  endpointsRefreshing: boolean;
  /** Why the catalog cannot be used; no grant is sent while it is set. */
  catalogProblem: string;
  onClose: () => void;
  /**
   * Called with the create response, which carries the one-time secret, and
   * the endpoint ids granted with it, or null when no catalog was usable and
   * so nothing could be chosen.
   */
  onCreated: (created: ExternalMutationResult, granted: string[] | null) => void;
}

/** Creates an API client in orgId, optionally with a role and endpoint grants. */
function ApiClientCreateModal({
  open,
  orgId,
  roles,
  rolesLoading,
  endpoints,
  endpointsLoading,
  endpointsRefreshing,
  catalogProblem,
  onClose,
  onCreated,
}: ApiClientCreateModalProps) {
  const [t] = useTranslation("global");
  const [name, setName] = useState("");
  const [roleId, setRoleId] = useState("");
  const [granted, setGranted] = useState<string[]>([]);
  const [error, setError] = useState("");
  const [postExternal] = usePostExternalMutation();
  // Set synchronously on the first submit and cleared when it settles: a
  // second Enter during the token wait, before the mutation is even
  // dispatched, would otherwise create a second client.
  const savingRef = useRef(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName("");
      setRoleId("");
      setGranted([]);
      setError("");
    }
  }, [open]);

  // While the catalog is read again Create waits, so it never sends an id
  // from the list that was there before. A catalog that cannot be read does
  // not stop a create: the client is made with no grant.
  const waitingForCatalog = endpoints !== null && endpointsRefreshing;
  const catalogUsable = endpoints !== null && !endpointsLoading && catalogProblem === "";

  const submit = async () => {
    // Enter reaches here without the OK button's loading guard.
    if (savingRef.current || waitingForCatalog) return;
    const trimmed = name.trim();
    if (trimmed === "") {
      setError(t("apiClients.nameRequired"));
      return;
    }
    savingRef.current = true;
    setSaving(true);
    setError("");
    // Only entries of the catalog just read, which the checklist shows.
    const active = new Set(
      (endpoints ?? []).filter((endpoint) => endpoint.active).map((endpoint) => endpoint.id),
    );
    const permissions = catalogUsable ? granted.filter((id) => active.has(id)) : [];
    try {
      const token = await GETJwtToken();
      const created = await postExternal({
        token,
        name: trimmed,
        orgId,
        roleId: roleId || undefined,
        permissions,
      }).unwrap();
      onCreated(created, catalogUsable ? permissions : null);
    } catch (err) {
      setError(apiClientErrorMessage(err, t("apiClients.genericError")));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title={t("apiClients.create")}
      okText={t("apiClients.save")}
      cancelText={t("apiClients.cancel")}
      onOk={submit}
      onCancel={saving ? undefined : onClose}
      closable={!saving}
      maskClosable={!saving}
      keyboard={!saving}
      confirmLoading={saving}
      okButtonProps={
        {
          "data-testid": "api-client-create-submit",
          disabled: waitingForCatalog,
        } as Record<string, unknown>
      }
      cancelButtonProps={
        { "data-testid": "api-client-create-cancel", disabled: saving } as Record<string, unknown>
      }
      destroyOnClose
      width={endpoints ? 720 : undefined}
    >
      <div data-testid="api-client-create-modal">
        {error && (
          <Alert
            type="error"
            showIcon
            message={error}
            className="mb-3"
            data-testid="api-client-create-error"
          />
        )}
        <span className="drawer__text">{t("apiClients.name")}</span>
        <Input
          data-testid="api-client-create-name"
          value={name}
          maxLength={255}
          placeholder={t("apiClients.namePlaceholder")}
          onChange={(event) => setName(event.target.value)}
          onPressEnter={submit}
          style={{ marginTop: 6 }}
        />
        <div className="mt-3">
          <span className="drawer__text">{t("apiClients.role")}</span>
          <div style={{ marginTop: 6 }}>
            <ApiClientRoleSelect
              testId="api-client-create-role"
              roles={roles}
              value={roleId}
              onChange={setRoleId}
              loading={rolesLoading}
            />
          </div>
        </div>
        {endpoints && (
          <div className="mt-3">
            <span className="drawer__text">{t("apiClients.endpoints")}</span>
            <p className="mt-1 mb-1">{t("apiClients.endpointsHelp")}</p>
            {!roleId && includesLiveRoute(granted, endpoints) && (
              <Alert
                type="warning"
                showIcon
                message={t("apiClients.liveNeedsRole")}
                className="mb-2"
                data-testid="api-client-create-live-needs-role"
              />
            )}
            <ApiClientEndpointChecklist
              testId="api-client-create-endpoints"
              endpoints={endpoints}
              value={granted}
              onChange={setGranted}
              loading={endpointsLoading}
              error={catalogProblem}
            />
          </div>
        )}
      </div>
    </Modal>
  );
}

export default ApiClientCreateModal;
